// File: src/routes/AppRoutes.jsx
// Description: Defines application routes and implements protected routes using RequireAuth.

import React, { useContext } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import MainLayout from '../layouts/MainLayout';

// Import pages
import LandingPage from '../pages/LandingPage';
import LoginPage from '../pages/LoginPage';
import RegisterPage from '../pages/RegisterPage';
import ForgotPasswordPage from '../pages/ForgotPasswordPage'; // New component for forgot password
import ResetPasswordPage from '../pages/ResetPasswordPage'; // New component for reset password
import DashboardPage from '../pages/DashboardPage';
import ProfileEditPage from '../pages/ProfileEditPage';
import ProfileQRPage from '../pages/ProfileQRPage';
import PublicProfilePage from '../pages/PublicProfilePage';
import SavedProfilesPage from '../pages/SavedProfilesPage';  // New saved profiles page
import NetworkingOpportunitiesPage from '../pages/NetworkingOpportunitiesPage'; // New networking opportunities page
import NotFoundPage from '../pages/NotFoundPage';
import MentorParkLayout from '../layouts/MentorParkLayout';
import MentorParkHome from '../pages/mentor-park/MentorParkHome';
import SkillGapAnalysisPage from '../pages/mentor-park/SkillGapAnalysisPage';
import CareerToolsPreparationPage from '../pages/mentor-park/CareerToolsPreparationPage';
import ResourcesCertificationsPage from '../pages/mentor-park/ResourcesCertificationsPage';
import CommunityNetworkingPage from '../pages/mentor-park/CommunityNetworkingPage';

// Import AI feature pages
import CareerInsightsPage from '../pages/CareerInsightsPage';
import AITestComponent from '../components/AITestComponent';
import AIProfilePersonalization from '../components/AIProfilePersonalization';
import AINetworkingAssistantPage from '../pages/AINetworkingAssistantPage';
import ContactsPage from '../pages/ContactsPage';
import ConversationsPage from '../pages/ConversationsPage';

import { AuthContext } from '../context/AuthContext';
import AdminPage from '../pages/AdminPage';

// Higher-order component to protect routes requiring authentication.
const RequireAuth = ({ children }) => {
  const { user } = useContext(AuthContext);
  // If not logged in, redirect to login page.
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  return children;
};

const AppRoutes = () => {
  return (
    <Routes>
      {/* Wrap all routes in the MainLayout */}
      <Route path="/" element={<MainLayout />}>
        {/* Public Routes */}
        <Route index element={<LandingPage />} />
        <Route path="login" element={<LoginPage />} />
        <Route path="register" element={<RegisterPage />} />
        <Route path="forgot-password" element={<ForgotPasswordPage />} />  {/* New forgot password route */}
        <Route path="reset-password" element={<ResetPasswordPage />} />  {/* New reset password route */}

        {/* Protected Routes */}
        <Route
          path="dashboard"
          element={
            <RequireAuth>
              <DashboardPage />
            </RequireAuth>
          }
        />
        <Route
          path="admin"
          element={
            <RequireAuth>
              <AdminPage />
            </RequireAuth>
          }
        />
        <Route
          path="profile"
          element={
            <RequireAuth>
              <ProfileEditPage />
            </RequireAuth>
          }
        />
        <Route
          path="profile/edit"
          element={
            <RequireAuth>
              <ProfileEditPage />
            </RequireAuth>
          }
        />
        <Route
          path="profile/qr"
          element={
            <RequireAuth>
              <ProfileQRPage />
            </RequireAuth>
          }
        />
        <Route
          path="saved-profiles"
          element={
            <RequireAuth>
              <SavedProfilesPage />
            </RequireAuth>
          }
        />
        <Route
          path="networking-opportunities"
          element={
            <RequireAuth>
              <NetworkingOpportunitiesPage />
            </RequireAuth>
          }
        />
        <Route
          path="contacts"
          element={
            <RequireAuth>
              <ContactsPage />
            </RequireAuth>
          }
        />

        <Route
          path="conversations"
          element={
            <RequireAuth>
              <ConversationsPage />
            </RequireAuth>
          }
        />

        {/* Mentor Park Routes */}
        <Route
          path="mentor-park"
          element={
            <RequireAuth>
              <MentorParkLayout />
            </RequireAuth>
          }
        >
          <Route index element={<MentorParkHome />} />
          <Route path="career-insights" element={<CareerInsightsPage />} />
          <Route path="skill-gap" element={<SkillGapAnalysisPage />} />
          <Route path="tools" element={<CareerToolsPreparationPage />} />
          <Route path="resources" element={<ResourcesCertificationsPage />} />
          <Route path="job-alignment" element={<Navigate to="/mentor-park/tools?tab=1" replace />} />
          <Route path="community" element={<CommunityNetworkingPage />} />
        </Route>

        {/* Redirect old Career Insights path */}
        <Route path="career-insights" element={<Navigate to="/mentor-park/career-insights" replace />} />
        <Route
          path="ai-test"
          element={
            <RequireAuth>
              <AITestComponent />
            </RequireAuth>
          }
        />
        <Route
          path="ai-profile-personalization"
          element={
            <RequireAuth>
              <AIProfilePersonalization />
            </RequireAuth>
          }
        />
        <Route
          path="ai-networking-assistant"
          element={
            <RequireAuth>
              <AINetworkingAssistantPage />
            </RequireAuth>
          }
        />

        {/* Public Profile route: viewable via QR code */}
        <Route path="u/:username" element={<PublicProfilePage />} />

        {/* 404 Not Found */}
        <Route path="*" element={<NotFoundPage />} />
      </Route>
    </Routes>
  );
};

export default AppRoutes;
